import { Component, OnDestroy, OnInit } from '@angular/core';

import { BehaviorSubject, combineLatest, debounceTime, filter, map, mergeMap, Observable, Subscription, take, tap } from 'rxjs';

import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';


import { getVIDateQueryInputOptions } from '../../../Monitor/Shared/models/monitoring-date-query-input-options';
import { ViMonitorService } from '../../services/vi-monitor.service';
import { defaultDateTypeOptions } from '../../../Common/Forms/components/DateRangeSelector/date-range-selector.component';
import { SearchRangeModel } from '../../../Monitor/Core/models/search-range.model';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { MonitorConfigService } from '../../../Common/MonitorShared/services/monitor-config.service';
import { SearchResultsService } from '../../../Monitor/Core/services/search-results.service';

import { ViReportsSearchService } from 'app/VisibilityIntelligence/services/vi-reports-search.service';

import { ReportSeriesModel } from '../../../Common/Reports/models/report-series.model';
import { SearchErrorTypeEnum } from '../../../Monitor/Shared/components/MonitoringResult/mentionSearchResult/mention-search-result.component';
import { InvalidFiltersError } from '../../../Monitor/Shared/errors/invalid-filters.error';
import { MediaSubscriptionStatusEnum } from '../../../Monitor/Shared/enums/media-subscription-status.enum';
import { ReportDateRangeModel } from '../../../Common/Reports/models/report-date-range.model';
import { SearchErrorsEnum } from '../../../Monitor/Core/enums/search-errors.enum';
import { SearchError } from '../../../Monitor/Core/errors/search.error';
import { SearchStateErrorModel } from '../../../Monitor/Core/models/search-state-error.model';
import { SearchStatesEnum } from '../../../Monitor/Shared/enums/search-states.enum';

import { RelativeTimezoneDateTypeEnum } from '../../../api/mentions/enum/relative-timezone-date-type.enum';
import { MediaTypeEnum } from '../../../Common/Basic/enums/media-type.enum';
import { MediaTypeModel } from '../../../Monitor/Shared/models/media-type.model';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';


@Component({
    selector: 'agpr-vi-reports',
    templateUrl: './vi-reports.component.html',
    styleUrls: ['./vi-reports.component.scss'],
    standalone: false
})
export class ViReportsComponent implements OnInit, OnDestroy {

    dateFilterOptions: DropdownOptionModel[] = getVIDateQueryInputOptions();
    dateTypeFilterOptions: DropdownOptionModel[] = defaultDateTypeOptions;
    searchRange: SearchRangeModel = new SearchRangeModel();
    dateRange: DateQueryModel = new DateQueryModel();

    filtersLocked: boolean = false;
    selectedTabIndex: number = 0;

    seriesMap: Map<any, ReportSeriesModel> = null;
    errorType: SearchErrorTypeEnum;
    filterError: InvalidFiltersError;
    initialFulfillSeriesCooldown: boolean = true;

    subscriptions: Subscription[] = [];
    pageHeading$: Observable<string>;
    pageSRHeading$: Observable<string>;
    dateFilterOptions$: Observable<DropdownOptionModel[]>;
    dateTypeFilterOptions$: Observable<DropdownOptionModel[]>;
    refreshing$: Observable<boolean>;

    private seriesMapReady$ = new BehaviorSubject<Map<any, ReportSeriesModel>>(null);
    private mentionSearchFilterReady: boolean = false;
    private mentionSearchFilterReady$ = new BehaviorSubject<boolean>(false);

    readonly severityEnum = messageSeverityEnum;
    readonly errorTypes = SearchErrorTypeEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly mediaSubscriptionStatus = MediaSubscriptionStatusEnum;

    constructor(
        public monitorConfigService: MonitorConfigService,
        public viReportsSearchService: ViReportsSearchService,
        public searchResultsService: SearchResultsService,
        private viMonitorService: ViMonitorService,
    )  {
        this.refreshing$ = this.viReportsSearchService.getIsFetchData();
    }

    ngOnInit() {
        const filterValidation = this.viReportsSearchService.getSearchState().subscribe(
            searchState => {
                this.filterError = null;
                this.errorType = SearchErrorTypeEnum.none;

                switch (searchState.type) {
                    case SearchStatesEnum.loading:
                        // this.isLoadingMentions = true;
                        break;
                    case SearchStatesEnum.error:
                        const errorState = searchState as SearchStateErrorModel;
                        if (errorState.error instanceof InvalidFiltersError) {
                            this.errorType = SearchErrorTypeEnum.filters;
                            this.filterError = errorState.error;
                        } else if (errorState.error instanceof SearchError) {
                            switch (errorState.error.type) {
                                case SearchErrorsEnum.queryTooLong:
                                    this.errorType = SearchErrorTypeEnum.queryLength;
                                    break;
                                case SearchErrorsEnum.discoverListNotExist:
                                    this.errorType = SearchErrorTypeEnum.discoverListNotExist;
                                    break;
                                case SearchErrorsEnum.unknown:
                                default:
                                    this.errorType = SearchErrorTypeEnum.unknown;
                                    break;
                            }
                        } else {
                            this.errorType = SearchErrorTypeEnum.unknown;
                        }
                        break;
                    default:
                }
            }
        );
        const filterChange = this.viReportsSearchService.getFilters().pipe(debounceTime(300)).subscribe(filters => {
            if (this.mentionSearchFilterReady && !this.initialFulfillSeriesCooldown) {
                this.viReportsSearchService.fetchDataForSeries(true);
            } else {
                this.mentionSearchFilterReady = true;
                this.mentionSearchFilterReady$.next(this.mentionSearchFilterReady);
            }
        });



        this.subscriptions.push(
            filterValidation,
            filterChange,
            this.viReportsSearchService.getDateQuery().subscribe(
                dateQuery => {
                    this.dateRange = dateQuery;
                    this.seriesMap?.forEach(series => series.dateRange = new ReportDateRangeModel().setFromDateQuery(this.dateRange));
                }
            ),
            this.monitorConfigService.getSearchRange().subscribe(
                searchRange => this.searchRange = searchRange
            ),
            combineLatest([
                this.viMonitorService.currentResource$.pipe(filter(x => !!x)),
                this.seriesMapReady$.pipe(filter(x => !!x)),
                this.mentionSearchFilterReady$.pipe(
                    filter(x => !!x),
                    take(1)
                )
            ]).subscribe(([currentResource, seriesMap, filterReady]) => {

                this.seriesMap = seriesMap;

                const criteria = new MentionSearchCriteriaModel(undefined, true);
                criteria.isMonitorSearch = true;
                criteria.query.aiAgent.include = [currentResource.agentId];
                criteria.query.dateQuery = null;
                // @HACK weird b/e LLM_RESPONSE is a MediaTypeEnum
                criteria.query.includedMediaTypes = [new MediaTypeModel('LLM_RESPONSE' as MediaTypeEnum)];

                this.viReportsSearchService.clearFilters();
                const dateRange = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_30_DAYS);
                dateRange.timezone = 'UTC';
                this.viReportsSearchService.setDateQuery(dateRange, true);
                this.viReportsSearchService.setSearchCriteria(criteria);
                this.viReportsSearchService.fulfillSeries(Array.from(this.seriesMap.values()));
                /**
                 * Note: setFilters are called in a lot of places, so this getFilters will be called many times,
                 * even after the initial fulfillSeries, and it is why we see fetchData twice on load.
                 * Therefore, we set a timed flag here to avoid the second fetch.
                 * Assuming user would not actually update filters in this period
                 */
                setTimeout(() => this.initialFulfillSeriesCooldown = false, 1000);
            })
        );

        this.filtersLocked = this.searchResultsService.filtersLocked();
        this.searchResultsService.setIsViewModeChange(false);
    }


    dateRangeChanged(dateRange: DateQueryModel): void {
        console.log('ignore date change for now');
        // this.viReportsSearchService.setDateQuery(dateRange);
        // this.viReportsSearchService.search(true);
        // this.viReportsSearchService.fetchDataForSeries(true);
    }

    clearFilters(doSearch: boolean = false): void {
        // this.filterChipsComponent?.chipContainer.chipPopover.hide();
        this.viReportsSearchService.clearFilters();
        this.filtersLocked = false;
        this.searchResultsService.setFiltersLocked(false);

        if (doSearch) {
            // refresh primary facets as well because this also resets the date query
            this.viReportsSearchService.search(true, true);
        }
    }

    toggleFilterLock() {
        this.filtersLocked = !this.filtersLocked;
        this.searchResultsService.setFiltersLocked(this.filtersLocked);
    }

    //#endregion

    fulfillSeries(seriesMap: Map<any, ReportSeriesModel>): void {
        this.viReportsSearchService.deregisterSeries();
        this.seriesMapReady$.next(seriesMap);
    }

    updateSeries(series: { oldSeriesTypes: any[]; newSeries: ReportSeriesModel[] }): void {
        const replacedSeries = [];
        series.oldSeriesTypes.forEach((x, i) => {
            const oldSeries = this.seriesMap.get(x);
            if (oldSeries) {
                replacedSeries.push(oldSeries);
                // assumption here is that oldSeriesTypes and newSeries have the same length
                this.seriesMap.set(x, series.newSeries[i]);
            }
        });
        this.viReportsSearchService.deregisterSeries(replacedSeries);
        this.viReportsSearchService.fulfillSeries(series.newSeries);
    }

    refresh(): void {
        this.viReportsSearchService.fetchDataForSeries();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());

        if (!this.searchResultsService.filtersLocked() && !this.searchResultsService.getIsViewModeChange()) {
            this.clearFilters();
        }
    }

}
